/**
 * PageHeader — the standard page title block.
 *
 * Supports a role-first greeting variant ("Good morning, {name}. Your queue is
 * ready.") per the design system's Role-First principle, plus an optional
 * subtitle and right-aligned actions slot.
 */
import type { CSSProperties, ReactNode } from 'react';
import { colors } from '../theme';

function timeGreeting(d = new Date()): string {
  const h = d.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

export interface PageHeaderProps {
  title: string;
  subtitle?: ReactNode;
  /** When set, renders a role-first greeting line above the title. */
  greetingName?: string;
  /** Trailing clause for the greeting (default: "Your queue is ready."). */
  greetingTail?: string;
  actions?: ReactNode;
  style?: CSSProperties;
}

export function PageHeader({
  title,
  subtitle,
  greetingName,
  greetingTail = 'Your queue is ready.',
  actions,
  style,
}: PageHeaderProps) {
  return (
    <header style={{ ...wrap, ...style }}>
      <div>
        {greetingName && (
          <p style={greeting}>
            {timeGreeting()}, {greetingName}. <span style={greetingTailStyle}>{greetingTail}</span>
          </p>
        )}
        <h1 style={titleStyle}>{title}</h1>
        {subtitle && <p style={subtitleStyle}>{subtitle}</p>}
      </div>
      {actions && <div style={actionsStyle}>{actions}</div>}
    </header>
  );
}

const wrap: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 16,
  marginBottom: 20,
};
const greeting: CSSProperties = {
  margin: '0 0 2px',
  fontSize: '1.05rem',
  fontWeight: 600,
  color: colors.textPrimary,
};
const greetingTailStyle: CSSProperties = { color: colors.textSecondary, fontWeight: 500 };
const titleStyle: CSSProperties = { margin: 0, fontSize: '1.5rem', fontWeight: 700, color: colors.textPrimary };
const subtitleStyle: CSSProperties = { margin: '6px 0 0', color: colors.textSecondary, fontSize: '0.92rem', maxWidth: 760 };
const actionsStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 };
